import apiClient, { createFormDataClient } from './apiClient';
import { fileToBase64, createFormData } from '../../utils/fileEncoding';
import { validateFileSecure } from '../../utils/fileValidation';

/**
 * Send a chat message with file contents to the backend
 * 
 * HISTORY_LOADING SUPPORT (Jan 31, 2026):
 * - Accepts historyMode flag to load conversation history instead of sending message
 * - historyMode = true: Load messages from conversation (empty message)
 * - historyMode = false (default): Send new message to conversation
 * - Enables unified message handling through chat API
 * 
 * CONVERSATION_ID SUPPORT:
 * - Accepts optional conversation_id parameter
 * - conversation_id = null: Starts new conversation (backend creates new)
 * - conversation_id = <string>: Continues existing conversation or load its history
 * - Backend returns conversation_id in response for future messages
 * - Enables chat history tracking and conversation persistence
 * 
 * MULTI-FILE UPLOAD CHANGES:
 * - Now validates each file before upload (security layer)
 * - Sends ALL selected files in SINGLE request (not multiple requests)
 * - Properly handles file wrapping/unwrapping for consistency
 * - Provides detailed error messages for validation failures
 * - Logs file transmission details for debugging
 * 
 * FILE FLOW:
 * 1. Input validation: Check message and files array
 * 2. Filter invalid files: Remove null/undefined entries
 * 3. Security validation: Check each file for zero-bytes, magic bytes, etc.
 * 4. Route to correct endpoint: /api/chat-upload (multipart) or /api/chat (JSON)
 * 5. CreateFormData or base64 encoding: Prepare payload
 * 6. Send single request with ALL files
 * 7. Handle response: Support both single-file and multi-file responses
 * 
 * @param {string} message - The message text (empty string if historyMode = true)
 * @param {Array} files - Array of File objects (may be wrapped as {file, name, ...} or raw File)
 * @param {Object} options - Configuration options
 * @param {string} options.mode - 'json' (base64) or 'multipart' (FormData)
 * @param {string|null} options.conversationId - Conversation ID or null for new chat
 * @param {boolean} options.historyMode - If true, load conversation history (send empty message)
 * @param {string} options.cursor - For pagination in history mode
 * @param {number} options.limit - Number of messages to return in history mode
 * @returns {Promise<Object>} - Response from backend with reply, conversation_id, and optional file status
 * @throws {Error} - Throws descriptive error message if validation or API call fails
 */
export const sendChatMessage = async (message, files = [], options = {}) => {
  try {
    const { mode = 'multipart', conversationId = null, historyMode = false, cursor = null, limit = 20 } = options;

    // WHY: Validate message type to provide clear error messages
    if (typeof message !== 'string') {
      throw new Error('Message must be a string');
    }

    // WHY: Validate files is array to prevent type errors (defensive programming)
    if (!Array.isArray(files)) {
      throw new Error('Files must be an array');
    }

    // HISTORY_MODE: Allow empty message when loading history
    // WHY: In history mode, we're not sending a message, just loading conversation messages
    if (!historyMode && !message.trim() && files.length === 0) {
      throw new Error('Message text or files are required');
    }

    // MULTI-FILE FIX: Filter out invalid files more comprehensively
    // Files may be wrapped as {file, name, size, ...} or raw File objects
    const validFiles = files.filter(file => {
      if (!file) {
        console.warn('[ChatAPI] Filtering out null/undefined file');
        return false;
      }
      // If wrapped, check if it has a valid File object inside
      if (file.file instanceof File) return true;
      // If not wrapped, check if it's directly a File object
      if (file instanceof File) return true;
      
      console.warn('[ChatAPI] Filtering out invalid file:', file);
      return false;
    });

    console.log(
      `[ChatAPI] Processing message with ${validFiles.length}/${files.length} valid files (mode: ${mode}, conversationId: ${conversationId || 'new'})`
    );

    // MULTI-FILE FIX: Security validation for each file BEFORE upload
    // WHY: Catch corruption, zero-byte, and spoofed files before they reach backend
    for (const file of validFiles) {
      try {
        const actualFile = file.file instanceof File ? file.file : file;
        const validation = await validateFileSecure(actualFile, {
          checkMagicBytes: true,
          checkRateLimit: false, // Don't rate limit in chat context
        });

        if (!validation.valid) {
          throw new Error(validation.error);
        }

        console.log(`[ChatAPI] Security validation passed for: ${actualFile.name}`);
      } catch (securityError) {
        console.error('[ChatAPI] File security validation failed:', securityError);
        throw new Error(`File security check failed: ${securityError.message}`);
      }
    }

    // Determine which send method to use based on file presence and mode
    if (validFiles.length === 0) {
      // No files - use standard JSON
      console.log('[ChatAPI] No files to send, using JSON mode');
      return await sendChatMessageJSON(message, [], conversationId, { historyMode, cursor, limit });
    }

    if (mode === 'multipart') {
      // MULTI-FILE FIX: Use multipart/form-data for file contents
      // WHY: Efficiently handles multiple files in single request
      console.log(`[ChatAPI] Sending ${validFiles.length} files via multipart/form-data`);
      return await sendChatMessageMultipart(message, validFiles, conversationId, { historyMode, cursor, limit });
    } else {
      // Use JSON with base64 encoded files
      console.log(`[ChatAPI] Sending ${validFiles.length} files via JSON with base64 encoding`);
      return await sendChatMessageJSON(message, validFiles, conversationId, { historyMode, cursor, limit });
    }
  } catch (error) {
    // WHY: Log full error for debugging but return user-friendly message
    console.error('[ChatAPI] Error sending message with files:', error);

    // Provide specific error messages based on HTTP status codes
    if (error.response?.status === 413) {
      throw new Error('File size too large. Maximum 100MB total per message.');
    } else if (error.response?.status === 400) {
      throw new Error(error.response.data?.message || 'Invalid request to server.');
    } else if (error.response?.status === 401) {
      throw new Error('Session expired. Please login again.');
    } else if (error.response?.status === 403) {
      throw new Error('Access denied. Please check your permissions.');
    } else if (error.response?.status === 415) {
      throw new Error('File type not supported by server.');
    } else if (error.response?.status === 500) {
      throw new Error('Server error. Please try again later.');
    } else if (error.response?.status === 503) {
      throw new Error('Service temporarily unavailable. Please try again later.');
    } else if (error.message) {
      // Re-throw with original error message
      throw error;
    } else {
      throw new Error('Failed to send message. Please check your connection.');
    }
  }
};

/**
 * Send message with files as JSON (base64 encoded)
 * WHY: Works with existing JSON API, but files must be < 5MB each
 * USAGE: For smaller files or when FormData not supported
 * 
 * HISTORY_MODE SUPPORT:
 * - Includes historyMode, cursor, limit in payload for history loading
 * - Empty message with historyMode=true loads conversation messages
 * 
 * @private
 * @param {string} message - The message text (can be empty in historyMode)
 * @param {Array} files - Array of files
 * @param {string|null} conversationId - Conversation ID or null for new chat
 * @param {Object} historyOptions - History loading options
 * @param {boolean} historyOptions.historyMode - If true, load history instead of sending
 * @param {string} historyOptions.cursor - For pagination
 * @param {number} historyOptions.limit - Number of messages
 */
const sendChatMessageJSON = async (message, files = [], conversationId = null, historyOptions = {}) => {
  const { historyMode = false, cursor = null, limit = 20 } = historyOptions;
  const fileData = [];

  // Convert files to base64 if present
  if (files.length > 0) {
    for (const file of files) {
      try {
        // Extract actual File object if wrapped
        const actualFile = file.file instanceof File ? file.file : file;

        // Check file size (base64 is ~33% larger)
        if (actualFile.size > 5 * 1024 * 1024) {
          console.warn(
            `[ChatAPI] File ${actualFile.name} is ${Math.round(actualFile.size / 1024 / 1024)}MB. ` +
            `Recommend using multipart mode for files > 5MB. Attempting base64 encoding...`
          );
        }

        // Convert to base64
        const base64Content = await fileToBase64(actualFile);

        fileData.push({
          name: actualFile.name,
          category: file.category || 'unknown',
          type: actualFile.type,
          size: actualFile.size,
          extension: file.extension || '',
          // Send file content as base64
          content: base64Content,
          encoding: 'base64',
        });
      } catch (err) {
        console.error(`[ChatAPI] Failed to encode file ${file.name}:`, err);
        throw new Error(`Failed to encode file ${file.name}: ${err.message}`);
      }
    }
  }

  // Build request payload with conversation_id and history mode support
  const payload = {
    message: message.trim(),
    files: fileData,
    transmissionMode: 'json',
    // ALWAYS include conversation_id: null means "create new conversation"
    conversation_id: conversationId,
  };

  // Add history mode options if in historyMode
  // WHY: Tells backend to load conversation history instead of processing as new message
  if (historyMode) {
    payload.historyMode = true;
    if (cursor !== null && cursor !== undefined) {
      payload.cursor = cursor;
    }
    if (limit !== undefined) {
      payload.limit = limit;
    }
  }

  // Make API request with file contents
  const response = await apiClient.post('/api/chat', payload);

  // WHY: Validate response structure to prevent crashes
  if (!response?.data) {
    throw new Error('Empty response from server');
  }

  if (typeof response.data !== 'object') {
    throw new Error('Invalid response format from server');
  }

  const data = response.data;

  // Handle file upload responses from backend
  // Backend may return: {file_id, upload_status} or {reply, filesProcessed}
  if (data.file_id && data.upload_status) {
    // Convert file upload status to user-friendly reply
    // Status flow: PENDING_S3 (in DB, awaiting S3 transfer) → READY (S3 transfer complete) or FAILED (transfer error)
    const statusMessages = {
      'PENDING_S3': `✓ File received and saved to database. Uploading to storage... (ID: ${data.file_id.substring(0, 8)}...)`,
      'READY': `✓ File successfully uploaded and ready for analysis!`,
      'FAILED': `✗ Failed to upload file. Please try again.`,
    };
    
    const statusMessage = statusMessages[data.upload_status] || `File status: ${data.upload_status}`;
    
    return {
      reply: statusMessage,
      file_id: data.file_id,
      upload_status: data.upload_status,
      filesProcessed: 1,
      conversation_id: data.conversation_id || undefined,
      title: data.title || undefined,
      agent_id: data.agent_id || undefined,
      message_count: data.message_count || 1,
    };
  }

  return {
    ...data,
    conversation_id: data.conversation_id || undefined,
  };
};

/**
 * Send message with files as multipart/form-data
 * 
 * HISTORY_MODE SUPPORT (Jan 31, 2026):
 * - Includes historyMode, cursor, limit in FormData for history loading
 * - Empty message with historyMode=true loads conversation messages
 * 
 * CONVERSATION_ID SUPPORT:
 * - Includes conversation_id in FormData
 * - null = new conversation (backend creates new)
 * - string = continue existing conversation
 * 
 * MULTI-FILE UPLOAD FLOW:
 * 1. Takes array of files (may be 1 to 10 files)
 * 2. Creates FormData with ALL files using proper indexing (fileMetadata[0], [1], [2]...)
 * 3. Includes conversation_id and history options in FormData
 * 4. Sends SINGLE multipart request to /api/chat-upload with all files
 * 5. Backend processes all files in loop (one request, multiple files)
 * 6. Returns status for each file or aggregated response with conversation_id
 * 
 * WHY multipart over JSON:
 * - Efficient binary transmission (no base64 overhead)
 * - Supports large files (no memory duplication)
 * - Direct stream to S3 possible (no intermediate encoding)
 * - Better for FormData with multiple large files
 * 
 * @private
 * @param {string} message - Chat message (can be empty in historyMode)
 * @param {Array} files - Array of files to upload (1-10 files)
 * @param {string|null} conversationId - Conversation ID or null for new chat
 * @param {Object} historyOptions - History loading options
 * @param {boolean} historyOptions.historyMode - If true, load history instead of sending
 * @param {string} historyOptions.cursor - For pagination
 * @param {number} historyOptions.limit - Number of messages
 * @returns {Promise<Object>} - Response with reply, conversation_id, and optional file statuses
 */
const sendChatMessageMultipart = async (message, files = [], conversationId = null, historyOptions = {}) => {
  const { historyMode = false, cursor = null, limit = 20 } = historyOptions;
  
  try {
    console.log(
      `[ChatAPI] Starting multipart upload with ${files.length} file(s), conversationId: ${conversationId || 'new'}, historyMode: ${historyMode}`
    );

    // MULTI-FILE FIX: Create FormData with ALL files
    // WHY: FormData.createFormData handles proper indexing and validation
    // Result: FormData with all files and metadata in single structure
    const formDataOptions = {
      message: message.trim(),
      transmissionMode: 'multipart',
      // ALWAYS include conversation_id: null means "create new conversation"
      conversation_id: conversationId,
      // Add history mode options if enabled
      ...(historyMode && { 
        historyMode: true,
        ...(cursor !== null && cursor !== undefined && { cursor }),
        ...(limit !== undefined && { limit }),
      }),
    };

    const formData = createFormData(files, formDataOptions);

    // MULTI-FILE FIX: Debug FormData for inspection
    // WHY: Help debug if files aren't reaching backend
    if (process.env.NODE_ENV === 'development') {
      // Count entries in FormData for debugging
      let formDataEntries = 0;
      // eslint-disable-next-line no-unused-vars
      for (let [key] of formData.entries()) {
        formDataEntries++;
      }
      console.log(`[ChatAPI] FormData has ${formDataEntries} entries (files + metadata)`);
    }

    // Use FormData-aware client
    const formDataClient = createFormDataClient();

    // MULTI-FILE FIX: Send SINGLE request with all files
    // WHY: Backend endpoint /api/chat-upload is configured with upload.array('files', 10)
    // which means it expects all files in SINGLE request, not multiple requests
    console.log(`[ChatAPI] Sending multipart request to /api/chat-upload with ${files.length} files`);
    const response = await formDataClient.post('/api/chat-upload', formData);

    // WHY: Validate response structure to prevent crashes
    if (!response?.data) {
      throw new Error('Empty response from server');
    }

    if (typeof response.data !== 'object') {
      throw new Error('Invalid response format from server');
    }

    const data = response.data;

    // NEW BACKEND FORMAT (UPDATED):
    // Backend may return:
    // 1. NEW FORMAT: {status: "success"|"failure", count: <number>, message: "<string>"}
    // 2. LEGACY FORMAT: {file_id, upload_status} or {uploadedFiles: [], failedFiles: []}
    // 3. AGENT RESPONSE: {reply, filesProcessed}
    
    // HANDLER 1: NEW RESPONSE FORMAT - {status, count, message}
    // WHY: Backend changed to use simpler response format
    if (data.status && (data.status === 'success' || data.status === 'failure')) {
      console.log(
        `[ChatAPI] NEW FORMAT response received: status=${data.status}, count=${data.count}, message="${data.message}"`
      );

      // Extract safe values with fallbacks for malformed responses
      const fileCount = Number.isInteger(data.count) ? data.count : 0;
      const backendMessage = typeof data.message === 'string' ? data.message : 'Unknown result';

      if (data.status === 'success') {
        // SUCCESS: Format user-friendly message
        // First line: show count of uploaded files
        // Second line: show backend message
        const reply = `✓ ${fileCount} file${fileCount !== 1 ? 's' : ''} uploaded successfully.\n${backendMessage}`;
        
        console.log(`[ChatAPI] Upload success: ${fileCount} files`);
        
        return {
          reply: reply,
          status: 'success',
          filesProcessed: fileCount,
          uploadedCount: fileCount,
          failedCount: 0,
          conversation_id: data.conversation_id || undefined,
          title: data.title || undefined,
          agent_id: data.agent_id || undefined,
          message_count: data.message_count || 1,
        };
      } else if (data.status === 'failure') {
        // FAILURE: Format error message for user
        const reply = `✗ Upload failed.\n${backendMessage}`;
        
        console.error(`[ChatAPI] Upload failure: ${backendMessage}`);
        
        throw new Error(reply);
      }
    }

    // HANDLER 2: LEGACY RESPONSE FORMAT - {file_id, upload_status}
    // WHY: Maintain backward compatibility with old backend format
    if (data.file_id && data.upload_status && files.length === 1) {
      console.log(`[ChatAPI] LEGACY FORMAT (single file) response: ${data.upload_status}`);
      
      const statusMessages = {
        'PENDING_S3': `✓ File received and saved to database. Uploading to storage... (ID: ${data.file_id.substring(0, 8)}...)`,
        'READY': `✓ File successfully uploaded and ready for analysis!`,
        'FAILED': `✗ Failed to upload file. Please try again.`,
      };
      
      const statusMessage = statusMessages[data.upload_status] || `File status: ${data.upload_status}`;
      
      return {
        reply: statusMessage,
        file_id: data.file_id,
        upload_status: data.upload_status,
        filesProcessed: 1,
        uploadedCount: 1,
        failedCount: 0,
        conversation_id: data.conversation_id || undefined,
        title: data.title || undefined,
        agent_id: data.agent_id || undefined,
        message_count: data.message_count || 1,
      };
    }

    // HANDLER 3: MULTI-FILE LEGACY FORMAT - {uploadedFiles, failedFiles}
    // WHY: Support old backend multi-file response format
    if (files.length > 1 && data.uploadedFiles) {
      console.log(
        `[ChatAPI] LEGACY FORMAT (multi-file) response: ${data.uploadedCount} uploaded, ${data.failedCount} failed`
      );

      // Generate status message based on upload results
      let statusMessage = '';
      if (data.uploadedCount === files.length) {
        statusMessage = `✓ All ${files.length} files uploaded successfully!`;
      } else if (data.uploadedCount > 0) {
        statusMessage = `⚠ Partial upload: ${data.uploadedCount}/${files.length} files uploaded successfully`;
      } else {
        statusMessage = `✗ All files failed to upload. Please try again.`;
      }

      return {
        reply: data.reply || statusMessage,
        filesProcessed: data.filesProcessed,
        uploadedCount: data.uploadedCount,
        failedCount: data.failedCount,
        uploadedFiles: data.uploadedFiles,
        failedFiles: data.failedFiles,
        conversation_id: data.conversation_id || undefined,
        title: data.title || undefined,
        agent_id: data.agent_id || undefined,
        message_count: data.message_count || 1,
      };
    }

    // DEFAULT: Return backend response as-is (agent response or unknown format)
    // WHY: Allow flexibility for other response formats
    console.log(`[ChatAPI] UNKNOWN FORMAT response, returning as-is:`, data);
    return {
      ...data,
      conversation_id: data.conversation_id || undefined,
    };

  } catch (error) {
    console.error('[ChatAPI] Multipart upload error:', error);

    // MULTI-FILE FIX: Provide context-aware error messages
    if (error.response?.status === 413) {
      throw new Error(
        'File(s) too large. Maximum 100MB total per message. ' +
        'Try uploading fewer files or smaller files.'
      );
    } else if (error.response?.status === 400) {
      throw new Error(
        error.response.data?.message ||
        'Invalid file upload request. Please check your files and try again.'
      );
    } else if (error.response?.status === 415) {
      throw new Error('One or more file types are not supported by the server.');
    }

    throw error;
  }
};
